import { isClientAction } from "duck/ui/chat/actions";
import { ChatMessageRole } from "duck/ui/chat/types";
import { processSubthreadCreation } from "duck/ui/chat/utils";
import { applyPatch, type Operation } from "fast-json-patch";

import { startSSE } from "./sse";
import type {
  DuckCommunicationProtocolOutput,
  Message,
  SendOptions,
  SSEController,
  SSEEvent,
} from "./types";
import { prepareFormData } from "./utils";

const CLIENT_ACTION_TOOL_NAME = "client_action";

export const sendMessage = ({
  url,
  input,
  headers,
  signal,
  logEvents = false,
  onEvent,
  onClientAction,
  safeMode,
  t,
}: SendOptions): SSEController => {
  let state: DuckCommunicationProtocolOutput = {
    messages: [],
    back_quack: {},
  };
  let doneReceived = false;
  // we intentionally use a Map which allows us to prevent race conditions
  // which could cause multiple same-threads creation attempts
  const subthreadCreations = new Map<string, Promise<void>>();
  const processedClientActionIds = new Set<string>();

  const handleOnEvent = (evt: SSEEvent) => {
    switch (evt.type) {
      case "open":
        onEvent({ type: "open" });
        break;
      case "message": {
        const { data, event } = evt.message;

        // Check if this is a "done" event
        if (event === "done") {
          if (logEvents) console.log("[protocol] done event received");

          doneReceived = true;
          onEvent({ type: "end" });
          break;
        }

        try {
          const parsed = JSON.parse(data);

          const ops: Operation[] = Array.isArray(parsed) ? parsed : [parsed];
          const res = applyPatch(
            structuredClone(state),
            ops,
            /*validate*/ false,
            /*mutateDocument*/ false
          );
          state = res.newDocument as DuckCommunicationProtocolOutput;
          if (logEvents) console.log("[protocol] patch", ops, "state", state);

          // Process subthread creation immediately when spawn_subthread tool responses arrive
          // Extract tool_response messages for spawn_subthread from the protocol output
          const toolResponseMessages = (state.messages ?? []).filter(
            (msg: Message) => msg.type === ChatMessageRole.TOOL_RESPONSE
          );

          if (toolResponseMessages.length > 0) {
            processSubthreadCreation(
              toolResponseMessages,
              input.thread_id,
              subthreadCreations,
              t,
              safeMode,
              url,
              input.viewport_is_mobile,
              onClientAction
            ).catch((err) => {
              console.error("Failed to process subthread creation:", err);
            });
          }

          // Dispatch client_action tool calls, deduplicating by message ID
          if (onClientAction) {
            for (const msg of state.messages ?? []) {
              if (
                msg.type === ChatMessageRole.TOOL_CALL &&
                msg.metadata?.tool_name === CLIENT_ACTION_TOOL_NAME &&
                !processedClientActionIds.has(msg.id)
              ) {
                const content = msg.content as Record<string, unknown> | null;
                const action = content?.action;
                if (isClientAction(action)) {
                  processedClientActionIds.add(msg.id);
                  onClientAction(action);
                }
              }
            }
          }

          onEvent({ type: "patch", ops, bytes: evt.bytes });
          onEvent({
            type: "update",
            protocolOutput: state,
            bytes: evt.bytes,
          });
        } catch (err) {
          if (logEvents)
            console.log("[protocol] parse error", err, "data:", data);

          onEvent({ type: "error", error: err });
        }
        break;
      }
      case "end":
        // Only emit "end" if we haven't already received a "done" message
        if (!doneReceived) {
          onEvent({ type: "end" });
        }

        break;
      case "error":
        onEvent({ type: "error", error: evt.error });
        break;
    }
  };

  const formData = prepareFormData(input);

  // For FormData requests, the browser must set multipart/form-data with boundary.
  // If Content-Type is preset (e.g., application/json), the server won't parse form fields.
  const normalizedHeaders = Object.fromEntries(
    Object.entries(headers ?? {}).filter(
      ([key]) => key.toLowerCase() !== "content-type"
    )
  );

  const controller = startSSE({
    url,
    method: "POST",
    headers: normalizedHeaders,
    body: formData,
    signal,
    onEvent: handleOnEvent,
  });

  return { abort: () => controller.abort() };
};
